import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth, roleHome } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "" });

  useEffect(() => {
    if (!loading && user) navigate(roleHome(user.role), { replace: true });
  }, [user, loading, navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register({ ...form, role: "client" });
      toast.success(`Welcome${u.firstName ? `, ${u.firstName}` : ""}!`);
      navigate(roleHome(u.role), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  let submitLabel = mode === "login" ? "Sign in" : "Create account";
  if (busy) submitLabel = "Please wait…";

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="inline-flex w-14 h-14 rounded-3xl zenith-cta items-center justify-center shadow-ambient-lg">
            <Leaf className="w-7 h-7 text-white" strokeWidth={1.5} />
          </span>
          <h1 className="mt-5 font-headline text-3xl font-light text-primary">
            {mode === "login" ? "Welcome back" : "Create your space"}
          </h1>
          <p className="text-on-surface-variant mt-2 text-sm">
            {mode === "login" ? "Sign in to continue your journey." : "Start your wellness journey in seconds."}
          </p>
        </div>

        <div className="bg-surface-container-lowest rounded-3xl p-7 sm:p-8 shadow-ambient-lg">
          <button
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-surface-container-low hover:bg-primary-fixed transition-colors duration-300 font-medium text-on-surface"
            data-testid="google-login-button"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-outline-variant/40" />
            <span className="text-xs text-outline uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-outline-variant/40" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 bg-surface-container-low rounded-2xl px-4 focus-within:bg-primary-fixed transition-colors duration-500">
                  <User className="w-4 h-4 text-outline" strokeWidth={1.5} />
                  <input required value={form.firstName} onChange={set("firstName")} placeholder="First name" className="flex-1 bg-transparent py-3.5 text-sm outline-none" data-testid="register-firstname" />
                </div>
                <div className="flex items-center gap-2 bg-surface-container-low rounded-2xl px-4 focus-within:bg-primary-fixed transition-colors duration-500">
                  <input required value={form.lastName} onChange={set("lastName")} placeholder="Last name" className="flex-1 bg-transparent py-3.5 text-sm outline-none" data-testid="register-lastname" />
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 bg-surface-container-low rounded-2xl px-4 focus-within:bg-primary-fixed transition-colors duration-500">
              <Mail className="w-4 h-4 text-outline" strokeWidth={1.5} />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" className="flex-1 bg-transparent py-3.5 text-sm outline-none" data-testid="login-email" />
            </div>
            <div className="flex items-center gap-2 bg-surface-container-low rounded-2xl px-4 focus-within:bg-primary-fixed transition-colors duration-500">
              <Lock className="w-4 h-4 text-outline" strokeWidth={1.5} />
              <input required type="password" value={form.password} onChange={set("password")} placeholder="Password" className="flex-1 bg-transparent py-3.5 text-sm outline-none" data-testid="login-password" />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl zenith-cta text-white font-medium hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {submitLabel}
              {!busy && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </form>

          <p className="text-center text-sm text-on-surface-variant mt-5">
            {mode === "login" ? "New to Vitalize?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>

        <p className="text-center text-xs text-outline mt-6">
          Demo — client: wendy@vitalize.com · coach: coach@vitalize.com · admin: admin@vitalize.com (password: Client123! / Coach123! / Admin123!)
        </p>
      </div>
    </div>
  );
}
